import { isNil } from 'ramda';
import React, { ReactNode } from 'react';
import { FieldArray } from 'react-final-form-arrays';
import { useTranslation } from 'react-i18next';
import styled, { css, FlattenSimpleInterpolation } from 'styled-components';
import { getSuggestedIncrementsInitialValues } from '~admin/view/components/create-event/helper/create-event-initial-values.helper';
import { NumberField } from '~admin/view/components/ui/form/fields/field-number.component';
import { TextField } from '~common/components/form/field-text.component';
import {
  TranslationField,
  TranslationFormData,
} from '~admin/view/components/ui/form/fields/field-translation.component';
import { Box } from '~common/components/box.component';
import { Button } from '~common/components/button/button.component';
import { Size } from '~common/components/button/button.helper';
import { Column } from '~common/components/column.component';
import { Span } from '~common/components/span.component';
import { Color } from '~common/model/basics';
import { PlusIcon } from '~web/view/components/ui/icon/plus.icon';

interface Props {
  name: string;
}

export interface SuggestedIncrementsFormData {
  title: TranslationFormData<string>[];
  value: number;
}

export const OfferGroupSuggestedIncrements: React.FunctionComponent<Props> = ({ name }) => {
  const { t } = useTranslation();

  return (
    <Container>
      <FieldArray name={`${name}.suggestedIncrements`}>
        {({ fields }): ReactNode => {
          const showRemoveButton = fields.length > 0;
          const disableAddButton = fields.length === 2;

          const onRemove = (): void => {
            fields.pop();
          };

          const onAdd = (): void => {
            fields.push(getSuggestedIncrementsInitialValues());
          };

          return (
            <>
              {!isNil(fields.length) && (
                <FieldGroupTitle>
                  {t('createEvent.offerGroupDetails.suggestedIncrements.title')}
                </FieldGroupTitle>
              )}
              <Fields>
                {fields.map((name: string, index: number) => (
                  <Row key={index}>
                    <RestrictBox>
                      <TranslationField
                        name={`${name}.title`}
                        label={t('createEvent.offerGroupDetails.suggestedIncrements.item.title')}
                        render={(renderProps): ReactNode => <TextField {...renderProps} />}
                      />
                    </RestrictBox>
                    <RestrictBox>
                      <NumberField
                        name={`${name}.value`}
                        label={t('createEvent.offerGroupDetails.suggestedIncrements.item.value')}
                        startAdornment={'$'}
                      />
                    </RestrictBox>
                  </Row>
                ))}
              </Fields>
              <ButtonContainer showRemoveButton={showRemoveButton}>
                {showRemoveButton && (
                  <Button color={Color.Gray} size={Size.Small} onClick={onRemove}>
                    {t('form.label.remove')}
                  </Button>
                )}
                <Button
                  color={Color.Gray}
                  size={Size.Small}
                  disabled={disableAddButton}
                  onClick={onAdd}
                >
                  <StyledPlusIcon width={12} height={12} />
                  {t('createEvent.offerGroupDetails.suggestedIncrements.add')}
                </Button>
              </ButtonContainer>
            </>
          );
        }}
      </FieldArray>
    </Container>
  );
};

const Container = styled(Column)`
  width: 100%;
`;

const Row = styled(Box)`
  width: 100%;
  border: 2px solid ${(props): string => props.theme.colors.gray.v3};
  border-radius: 16px;
  padding-top: 16px;
  padding-bottom: 32px;
  margin-bottom: 18px;
`;

const RestrictBox = styled(Box)`
  flex: 1 1 auto;
  margin: 0 12px;
`;

const FieldGroupTitle = styled(Span)`
  font-weight: bold;
  font-size: 18px;
  line-height: 22px;
  margin: 18px 12px 12px;
`;

const Fields = styled(Column)`
  > div:not(:first-child) {
    margin-top: 24px;
  }
`;

const ButtonContainer = styled(({ showRemoveButton, ...rest }) => <Box {...rest} />)<{
  showRemoveButton: boolean;
}>`
  margin-top: 16px;
  ${(props): FlattenSimpleInterpolation =>
    props.showRemoveButton
      ? css`
          justify-content: space-between;
        `
      : css`
          justify-content: flex-end;
        `};
`;

const StyledPlusIcon = styled(PlusIcon)`
  fill: currentColor;
  margin-right: 4px;
`;



// WEBPACK FOOTER //
// ./view/components/details/offer-group-details/offer-group-suggested-increments.component.tsx